import React, { createContext, useContext, useMemo } from "react";
import { useEnterpriseAuth } from "@/enterprise/hooks/useEnterpriseAuth";

type Theme = {
  realm: "enterprise" | "gov" | "health" | "unknown";
  ledHue: string;        // e.g., "cyan", "amber"
  glassTint: string;     // tailwind class fragment
  accentBorder: string;  // tailwind
};

const ThemeCtx = createContext<Theme>({
  realm: "unknown",
  ledHue: "cyan",
  glassTint: "from-slate-950 via-slate-900 to-black",
  accentBorder: "border-white/10",
});

export function useEnterpriseTheme() {
  return useContext(ThemeCtx);
}

export function EnterpriseThemeProvider({ children }: { children: React.ReactNode }) {
  const { realmId } = useEnterpriseAuth();

  const theme = useMemo<Theme>(() => {
    switch (realmId) {
      case "enterprise":
        return { realm: "enterprise", ledHue: "cyan", glassTint: "from-slate-950 via-slate-900 to-black", accentBorder: "border-cyan-400/30" };
      case "gov":
        return { realm: "gov", ledHue: "amber", glassTint: "from-[#0b0f14] via-[#0b0e12] to-black", accentBorder: "border-yellow-300/30" };
      case "health":
        return { realm: "health", ledHue: "emerald", glassTint: "from-teal-950 via-slate-900 to-black", accentBorder: "border-emerald-400/30" };
      default:
        return { realm: "unknown", ledHue: "cyan", glassTint: "from-slate-950 via-slate-900 to-black", accentBorder: "border-white/10" };
    }
  }, [realmId]);

  return <ThemeCtx.Provider value={theme}>{children}</ThemeCtx.Provider>;
}
